import {useAllPayeesQuery} from '@transactions/graphql';
import {CornerDownLeft, Sparkles} from 'lucide-react';
import * as React from 'react';
import {TextInput} from 'src/inputs/TextInput';
import {cn} from 'src/lib/utils';
import Loading from 'src/Loading';
import {Popover} from 'src/Popover/NewPopover';
import {PopoverContents, PopoverItem} from 'src/Popover/views';
import {useSearchPopover} from 'src/SearchPopover/useSearchPopover';
import {useSearch} from 'src/useSearch';
import type {FocusControls} from 'src/FocusTrap';
import type {SubmitState} from 'src/SearchPopover/useSearchPopover';

// A payee from the raw selection's categorization history, pinned above the
// regular typeahead results. `detail` is the provenance shown on the row
// (e.g. "9 of 14").
export type PayeeSuggestion = {
  name: string;
  detail: string;
};

type Option =
  | string
  | {type: 'create'; value: string}
  | {type: 'suggested'; value: string; detail: string};

const optionToString = (option: Option): string => {
  if (typeof option === 'string') {
    return option;
  } else {
    return option.value;
  }
};

const optionKey = (option: Option): string =>
  typeof option === 'string' ? `payee:${option}` : `${option.type}:${option.value}`;

const renderOption = (option: Option) => {
  if (typeof option === 'string') {
    return option;
  } else if (option.type === 'suggested') {
    return (
      <div className="flex w-full min-w-0 items-center gap-2">
        <Sparkles className="size-3.5 shrink-0" />
        <span className="min-w-0 flex-1 truncate">{option.value}</span>
        <span className="text-muted-foreground shrink-0 text-xs">
          {option.detail}
        </span>
      </div>
    );
  } else {
    return (
      <div className="flex items-center gap-2">
        <CornerDownLeft className="size-4" />
        Add "{option.value}"
      </div>
    );
  }
};

type Props = {
  autoFocus?: boolean;
  value: string;
  onChange: (payee: string) => void;
  // Pinned above the regular results — for the empty query and whenever they
  // match the typed query — so historical matches are one keystroke away.
  suggestions?: PayeeSuggestion[];
  // Rendered inside the input's right edge (e.g. auto-apply provenance).
  adornment?: React.ReactNode;
  inputClassName?: string;
  // Fired on the user's first keystroke — before the text commits into
  // `value` on submit/blur — so a caller can stop a late suggestion
  // response from overwriting in-progress input.
  onEdited?: () => void;
};

const PayeeInput = (props: Props) => {
  const {
    autoFocus,
    value,
    onChange,
    suggestions,
    adornment,
    inputClassName,
    onEdited,
  } = props;

  const activeItemRef = React.useRef<HTMLDivElement | null>(null);
  const anchorRef = React.useRef<HTMLInputElement | null>(null);
  const popoverRef = React.useRef<HTMLDivElement | null>(null);
  const focusControlsRef = React.useRef<FocusControls | null>(null);
  // Submissions move focus out of the FocusTrap, which fires onFocusOutside
  // synchronously before the popover unmounts; this flag stops that from
  // re-committing the raw query over the just-committed selection
  const justSubmittedRef = React.useRef(false);

  const {loading, data} = useAllPayeesQuery();
  const {search} = useSearch(data?.allPayees || [], []);


  const handleSearch = (query: string): Option[] => {
    const q = query.trim().toLowerCase();
    const suggested = (suggestions ?? [])
      .filter((s) => q === '' || s.name.toLowerCase().includes(q))
      .map(
        (s): Option => ({type: 'suggested', value: s.name, detail: s.detail}),
      );
    const suggestedNames = new Set(suggested.map(optionToString));
    // useSearch ignores the limit for the empty query (it returns allItems
    // verbatim), and the empty state is now the common open state — cap it
    // here so a large payee history can't stall the popover.
    const results = search(query, {limit: 80})
      .filter((name) => !suggestedNames.has(name))
      .slice(0, 80);
    const combined: Option[] = [...suggested, ...results];
    // No create row for an empty query — "Add \"\"" is never a real option,
    // and the empty query is now common since the payee no longer prefills.
    if (
      query.trim() !== '' &&
      !results.includes(query) &&
      !suggestedNames.has(query)
    ) {
      combined.push({type: 'create', value: query});
    }
    return combined;
  };

  const handleSubmit = (state: SubmitState<Option>) => {
    // Picking an option (click, or arrow/Tab selection) is a manual choice
    // just like typing, so it must also drop auto-apply provenance — while
    // a blur/tab-through commit of unchanged text (selection null) is not
    // an edit and must leave it alone.
    if (state.selection !== null) {
      onEdited?.();
    }
    if (state.selection === null) {
      onChange(state.query);
    } else {
      onChange(optionToString(state.selection));
    }
    closePopover();
    justSubmittedRef.current = true;
    if (state.focusDirection === 'backward') {
      focusControlsRef.current?.focusBackward();
    } else {
      focusControlsRef.current?.focusForward();
    }
  };

  const {
    isOpen,
    query,
    searchResults,
    selectedIndex,
    handleKeyDown,
    handleQueryChange,
    openPopover,
    closePopover,
    clearSelection,
  } = useSearchPopover<Option>({
    initialQuery: value,
    focusControlsRef,
    search: handleSearch,
    onSubmit: handleSubmit,
    // Focusing a filled input is browsing, not typing: suggestions whose
    // names merely contain the current value sort above it, so Tab
    // auto-submitting the first result would silently replace a committed
    // "Amazon" with a suggested "Amazon Marketplace". Only a query the
    // user actually changed auto-submits its top match on Tab.
    autoSubmitFirstOnTab: (query) =>
      query.trim() !== '' && query.trim() !== value.trim(),
  });

  React.useEffect(() => {
    if (isOpen) {
      justSubmittedRef.current = false;
    }
  }, [isOpen]);

  // Suggestions arrive asynchronously and prepend rows without changing the
  // query; an index-based keyboard highlight would silently move to a
  // different option, so drop it when the suggestion set changes.
  React.useEffect(() => {
    clearSelection();
  }, [suggestions, clearSelection]);

  // An autofocused mount fires onFocus before the user has done anything;
  // opening the typeahead then covers the fields below the moment the form
  // appears. Swallow that first focus — clicking, typing, or tabbing back
  // in still opens the popover as usual.
  const suppressInitialOpenRef = React.useRef(autoFocus === true);
  const handleFocus = () => {
    if (suppressInitialOpenRef.current) {
      suppressInitialOpenRef.current = false;
      return;
    }
    openPopover();
  };

  // Commit typed text when focus leaves the input (outside click/focus),
  // matching the Enter/Tab behavior — otherwise text typed without an
  // explicit selection is silently dropped
  const commitQuery = () => {
    if (justSubmittedRef.current) {
      justSubmittedRef.current = false;
      closePopover();
      return;
    }
    onChange(query);
    closePopover();
  };

  React.useLayoutEffect(() => {
    if (isOpen && anchorRef.current && popoverRef.current) {
      popoverRef.current.style.width = `${anchorRef.current.offsetWidth}px`;
    }
  }, [anchorRef.current, popoverRef.current, isOpen]);

  React.useLayoutEffect(() => {
    activeItemRef.current?.scrollIntoView({block: 'nearest'});
  }, [selectedIndex]);

  return (
    <>
      <div className="relative">
        <TextInput
          ref={anchorRef}
          value={query}
          onChange={(next) => {
            onEdited?.();
            handleQueryChange(next);
          }}
          onClick={openPopover}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          autoFocus={autoFocus}
          selectOnFocus
          placeholder="Payee"
          enterKeyHint="next"
          className={cn(adornment != null && 'pr-20', inputClassName)}
        />
        {adornment != null && (
          <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
            {adornment}
          </div>
        )}
      </div>
      <Popover
        anchorRef={anchorRef}
        anchor="bottom-left"
        focusControlsRef={focusControlsRef}
        open={isOpen}
        onOutsideClick={commitQuery}
        onOutsideFocus={commitQuery}
        passThroughOutsideClicks
      >
        <PopoverContents ref={popoverRef}>
          {loading ? (
            <Loading />
          ) : (
            searchResults.map((result, index) => (
              <PopoverItem
                key={optionKey(result)}
                isSelected={selectedIndex === index}
                ref={selectedIndex === index ? activeItemRef : null}
                onClick={() => handleSubmit({query, selection: result})}
              >
                {renderOption(result)}
              </PopoverItem>
            ))
          )}
        </PopoverContents>
      </Popover>
    </>
  );
};

export default PayeeInput;
